import type {
  AdminAuditEntryDto,
  AdminAuditPageDto,
  AdminFunnelDto,
  UserDto,
  AssessmentDto,
  PairViewDto,
  PaymentDto,
  Tier,
} from '@neke/core';

export type { AdminAuditEntryDto, AdminAuditPageDto, AdminFunnelDto };

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Access-токен держим в памяти, а не в localStorage: refresh лежит в
 * httpOnly-куке, и восстановить сессию можно через /auth/refresh.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Сюда сессия подписывается на смерть refresh-куки: обновиться не вышло —
 * человека надо вернуть на вход, а не крутить повторы. Через callback, а не
 * прямым импортом стора: стор сам импортирует этот модуль.
 */
let onSessionExpired: () => void = () => undefined;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Отказ по согласию: сервер закрыл маршруты анкеты, пока согласие не принято
 * (`consent.ts` в API, код `consent_required`, статус 403). Раньше согласие
 * проверял только клиент, и его представление могло разойтись с базой —
 * значит, отказ этот штатный, и показывать его текстом ошибки незачем: человека
 * надо просто увести подписывать.
 */
// Отвечает boolean, а не сужает тип: иначе в ветке «не про согласие» ошибка,
// уже объявленная как ApiError, схлопнулась бы в never.
export function isConsentRequired(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.code === 'consent_required';
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  // Токен, с которым запрос уходит: по нему потом видно, свой это 401 или уже
  // устаревший — пара могла обновиться, пока запрос летел.
  const sentWith = accessToken;

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...init.headers,
    },
  });

  // Токен живёт 15 минут: один прозрачный refresh, потом уже ошибка. Повтор
  // уходит с retry = false, поэтому второй 401 подряд второго круга не даст.
  if (res.status === 401 && retry && path !== '/api/auth/refresh') {
    // Заголовки собираются заново из модульного accessToken, так что повтор
    // берёт свежий токен, а не тот, с которым запрос уходил.
    if (await tokenForRetry(sentWith)) return request<T>(path, init, false);
  }

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => null)) as
    { error?: { code: string; message: string; details?: unknown } } | T | null;

  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown',
      err?.message ?? 'Ошибка запроса',
      err?.details,
    );
  }

  return body as T;
}

/**
 * Скачивание файла. Токен живёт в памяти, поэтому обычной ссылкой файл не
 * забрать — идём тем же fetch и с тем же прозрачным refresh, что и остальные
 * запросы, а наружу отдаём Blob и имя из Content-Disposition.
 */
async function requestBlob(
  path: string,
  retry = true,
): Promise<{ blob: Blob; filename: string }> {
  const sentWith = accessToken;

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (res.status === 401 && retry) {
    if (await tokenForRetry(sentWith)) return requestBlob(path, false);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Не удалось скачать файл',
    );
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const filename = utf8 ? decodeURIComponent(utf8[1]!) : (plain?.[1] ?? 'neke-report.pdf');

  return { blob: await res.blob(), filename };
}

interface TokenPair {
  accessToken: string;
  expiresIn: number;
  user: UserDto;
}

/**
 * Обновление сессии.
 *
 * Refresh лежит в httpOnly-куке, и сервер её ротирует: каждое обновление
 * отзывает прежнюю сессию, а повторный приход с уже отозванной кукой считается
 * кражей токена и гасит все сессии человека разом. Значит, два параллельных
 * обновления одной вкладкой — это выход из аккаунта: второе уйдёт с той же
 * кукой, что и первое, только уже отозванной.
 *
 * Поэтому обновление тут ровно одно на всех: `refreshInFlight` держит текущий
 * запрос, параллельные вызовы ждут его же, и промис сбрасывается, как только
 * запрос завершился, — следующее протухание токена обновится заново.
 *
 * Двух вкладок это не лечит: куку они делят, а память JS — нет. Столкновение
 * вкладок закрывается на сервере окном снисхождения к повтору; городить
 * межвкладочные локи в браузере под это не нужно.
 */
let refreshInFlight: Promise<TokenPair> | null = null;

async function runRefresh(): Promise<TokenPair> {
  try {
    const data = await request<TokenPair>('/api/auth/refresh', { method: 'POST' }, false);
    setAccessToken(data.accessToken);
    return data;
  } catch (e) {
    // 401/403 — кука мертва (истекла, отозвана, сессию погасили). Сессии конец:
    // чистим токен и отдаём человека на вход, вместо новых попыток.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      setAccessToken(null);
      onSessionExpired();
    }
    // Остальное (сеть отвалилась, 5xx) сессию не хоронит: токены не трогаем,
    // запрос просто не удался.
    throw e;
  }
}

export function refresh(): Promise<TokenPair> {
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Токен для повтора после 401.
 *
 * Если пока запрос летел, пара уже обновилась (соседний 401 успел раньше и
 * refresh к этому моменту уже завершился), нового обновления не запускаем —
 * оно ушло бы с отозванной кукой и выкинуло бы человека отовсюду. Просто берём
 * текущий токен.
 */
async function tokenForRetry(sentWith: string | null): Promise<string | null> {
  if (accessToken && accessToken !== sentWith) return accessToken;
  try {
    return (await refresh()).accessToken;
  } catch {
    // Обновиться не вышло. Отдаём исходный 401 наверх: страница покажет ошибку
    // или уведёт на вход — но повтора не будет.
    return null;
  }
}

/**
 * Вход и регистрация возвращают ту же пару, что и обновление, и так же ставят
 * refresh httpOnly-кукой. Access-токен кладётся в модульную переменную здесь, а
 * не на странице: забыть это на одном из двух вызовов — значит получить
 * «вошёл, но каждый запрос анонимный».
 */
async function startSession(path: string, body: unknown): Promise<TokenPair> {
  const data = await request<TokenPair>(path, { method: 'POST', body: JSON.stringify(body) });
  setAccessToken(data.accessToken);
  return data;
}

export const api = {
  health: () => request<{ status: string; version: number }>('/api/health'),

  /** Регистрация по номеру телефона. Номер сервер приводит к E.164 сам. */
  register: (body: { phone: string; password: string; name: string }) =>
    startSession('/api/auth/register', body),

  /** Вход по номеру телефона и паролю. */
  loginByPhone: (body: { phone: string; password: string }) =>
    startSession('/api/auth/login', body),

  logout: async () => {
    await request<void>('/api/auth/logout', { method: 'POST' });
    setAccessToken(null);
  },

  config: () => request<PublicConfig>('/api/config'),

  me: () => request<UserDto>('/api/me'),

  acceptConsent: (version: string) =>
    request<UserDto>('/api/me/consent', { method: 'POST', body: JSON.stringify({ version }) }),

  updateMe: (patch: {
    sex?: 'M' | 'F';
    locale?: 'ru' | 'kk';
    contact?: string | null;
    optInMatching?: boolean;
  }) => request<UserDto>('/api/me', { method: 'PATCH', body: JSON.stringify(patch) }),

  startAssessment: () => request<AssessmentDto>('/api/assessments', { method: 'POST' }),

  latestAssessment: () => request<AssessmentDto>('/api/assessments/me/latest'),

  saveAnswers: (id: string, answers: Array<{ index: number; value: number | string | null }>) =>
    request<AssessmentDto>(`/api/assessments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ answers }),
    }),

  completeAssessment: (id: string) =>
    request<AssessmentDto>(`/api/assessments/${id}/complete`, { method: 'POST' }),

  // recipientName — приватная пометка автора: кому он отдаёт ссылку.
  // Получателю она не показывается.
  createInvite: (recipientName: string) =>
    request<{
      id: string;
      token: string;
      url: string;
      recipientName: string;
      expiresAt: string;
    }>('/api/pairs/invite', {
      method: 'POST',
      body: JSON.stringify({ recipientName }),
    }),

  myInvites: () => request<InviteHistoryItem[]>('/api/pairs/invites'),

  acceptInvite: (token: string) =>
    request<PairViewDto>(`/api/pairs/accept/${token}`, { method: 'POST' }),

  pairReport: (id: string) => request<PairViewDto>(`/api/pairs/${id}/report`),

  // Файл, а не JSON: отдельная функция, потому что общий request разбирает
  // тело как JSON и на PDF споткнулся бы.
  reportPdf: (pairId: string) => requestBlob(`/api/pairs/${pairId}/report.pdf`),

  startPayment: (pairId: string, tier: Tier) =>
    request<PaymentDto>('/api/payments', {
      method: 'POST',
      body: JSON.stringify({ pairId, tier }),
    }),

  paymentStatus: (id: string) => request<PaymentDto>(`/api/payments/${id}`),

  myMatches: () => request<MatchListItem[]>('/api/matches/me'),

  adminPool: () => request<AdminPool>('/api/admin/pool'),

  adminRecompute: () =>
    request<{ evaluated: number; passed: number; rejected: number }>(
      '/api/admin/matches/recompute',
      { method: 'POST' },
    ),

  adminMatches: () => request<AdminMatch[]>('/api/admin/matches'),

  adminDecide: (id: string, status: 'APPROVED' | 'REJECTED' | 'SENT') =>
    request<{ id: string; status: string }>(`/api/admin/matches/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  adminReports: () => request<AdminGrant[]>('/api/admin/reports'),

  // ref — что угодно, что администратор скопировал: ссылка на отчёт,
  // ссылка-приглашение или голый id пары. Разбирает сервер.
  adminReportOpen: (ref: string, note?: string) =>
    request<AdminGrant>('/api/admin/reports', {
      method: 'POST',
      body: JSON.stringify({ ref, ...(note ? { note } : {}) }),
    }),

  adminReportClose: (pairId: string) =>
    request<AdminGrant>(`/api/admin/reports/${pairId}`, { method: 'DELETE' }),

  // days отсутствует — сервер считает за всё время. Так и запрашиваем на
  // «за всё время»: пустой параметр в адресе сервер прочитал бы как ошибку
  // разбора, а days=0 схема отвергает.
  adminFunnel: (days?: number) =>
    request<AdminFunnelDto>(`/api/admin/funnel${days ? `?days=${days}` : ''}`),

  // cursor — id последней уже показанной строки: журнал пополняется прямо во
  // время прокрутки, и постраничный offset терял бы записи.
  adminAudit: (scope: 'admin' | 'all', cursor?: string) =>
    request<AdminAuditPageDto>(
      `/api/admin/audit?scope=${scope}${cursor ? `&cursor=${cursor}` : ''}`,
    ),
};

export interface PublicConfig {
  googleEnabled: boolean;
  /** Вход по почте и паролю. Веб его не показывает — поле про мобильное
   * приложение, но сервер отдаёт его всем, и тип обязан это описывать. */
  nativeAuthEnabled: boolean;
  consentVersion: string;
  paymentsEnabled: boolean;
  tiers: Array<{ tier: Tier; price: number }>;
}

export interface MatchListItem {
  id: string;
  candidate: { id: string; name: string; age: number | null; city: string; contact: string | null };
  score: number;
  tier: 'introduce' | 'possible' | 'neutral' | 'rejected';
  parts: Record<string, number>;
  roleModel: { id: string; score: number };
  reasons: Array<{
    code: string;
    tone: 'plus' | 'minus';
    params?: Record<string, string | number>;
  }>;
  flags: Array<{ code: string; subjectId: string | null; hard: boolean }>;
  status: string;
}

/** Строка истории отчётов: одна пара глазами того, кто на неё смотрит. */
export interface InviteHistoryItem {
  id: string;
  /** owner — ссылку выдал зритель, partner — зритель по ней пришёл. */
  role: 'owner' | 'partner';
  /** Ссылка-приглашение: только у автора, принявшему её больше не показывают. */
  token: string | null;
  url: string | null;
  /** Приватная пометка автора; у принявшего всегда null. */
  recipientName: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  /** Второй участник пары — с точки зрения зрителя. */
  partner: { name: string } | null;
  partnerCompletedTest: boolean;
  /** Тест прошли оба: только тогда отчёт по паре соберётся. */
  reportReady: boolean;
  /** Полный разбор куплен лично зрителем — можно читать и скачивать PDF. */
  hasFullAccess: boolean;
  /** Отчёт купил второй участник. Доступа не даёт: покупка личная. */
  partnerPaid: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface AdminPool {
  men: number;
  women: number;
  people: Array<{
    id: string;
    name: string;
    sex: 'M' | 'F';
    age: number | null;
    city: string;
    stems: string[];
  }>;
}

/** Строка реестра отчётов, открытых вручную (оплата мимо Kaspi). */
export interface AdminGrant {
  pairId: string;
  tier: Tier;
  note: string | null;
  grantedAt: string;
  /** null — доступ открыт; дата — закрыт. */
  revokedAt: string | null;
  grantedByName: string;
  /** false — вторая половина ещё не приняла приглашение, отчёт не соберётся. */
  pairReady: boolean;
  /** По паре есть успешная оплата Kaspi — грант ей не нужен. */
  hasPayment: boolean;
  reportUrl: string;
  /**
   * Чем человек вошёл: почта у пришедших через Google, номер у
   * зарегистрировавшихся по телефону. Заполнено всегда одно из двух — что
   * именно, показывает `loginOf`.
   */
  a: { name: string; email: string | null; phone: string | null };
  b: { name: string; email: string | null; phone: string | null } | null;
}

export interface AdminMatch {
  id: string;
  score: number;
  tier: string;
  status: 'NEW' | 'APPROVED' | 'REJECTED' | 'SENT';
  a: { id: string; name: string };
  b: { id: string; name: string };
  contacts: { a: string | null; b: string | null } | null;
  breakdown: {
    parts: Record<string, number>;
    roleModel: { id: string; score: number };
    reasons: Array<{
      code: string;
      tone: 'plus' | 'minus';
      params?: Record<string, string | number>;
    }>;
    flags: Array<{ code: string; subjectId: string | null; hard: boolean }>;
  };
}
